import type { RouteSeo, SeoImage, SeoSite } from '@kkf/design'
import { TOURS } from './tours'

/**
 * King of Kings Garage's site tree.
 *
 * **This file is loaded by Node** — `vite.config.ts` imports it so the build can write
 * each route's metadata into a real HTML file — so it may hold no *value* import from
 * `@kkf/design`. `import type` is erased and is fine; a relative import of a data module
 * that follows the same rule is fine too, which is why the tours are read straight out of
 * the file that defines them. `data/garage.ts` cannot be read here: it
 * imports `appUrl`, and a value import would be handed to Node, which cannot parse the
 * `.tsx` the design system's barrel re-exports.
 *
 * `App.tsx` hands the same list to `AppShell`, so the head a crawler is served and the
 * head a visitor navigates into come from one place.
 *
 * ## It reads `.en`, and the pre-rendered head is English only
 *
 * The data modules became bilingual on 2026-09-18, so a tour's name and its
 * description are now `Text` — an object with a string per language. This file reads `.en`
 * off them **explicitly**, which needs no import at all because `Text` is a plain object
 * and `import type` is erased.
 *
 * That is a real limitation and not a shortcut. `seoAssets` writes ONE HTML file per route
 * at build time, so there is one `<title>` and one description per URL, and they are
 * English. A Mongolian reader gets the translated page — the shell applies the route's head
 * on navigation and every word of the body comes from the catalogue — but the document a
 * crawler or a link preview fetches is the English one.
 *
 * Fixing it properly means per-locale routes (`/mn/tours/...`), an HTML file per locale and
 * `hreflang` linking them, which is a piece of work with its own decisions — the biggest
 * being whether the Mongolian pages get their own URLs at all. Recorded in the session
 * brief rather than half-done here.
 *
 * ## What this app is trying to be found for
 *
 * Three different searches, and the site tree is arranged around them rather than around
 * the nav: *off-road recovery near a route in Mongolia* (`/services`, and the home page's
 * recovery chip), *an off-road tour or ADV training in Mongolia* (`/tours` and its six
 * pages), and *vocational training for a teenager* (`/programmes`). The third is the
 * mission and the first two pay for it, which is exactly what the copy says.
 */

const IMAGES = {
  trail: {
    url: '/assets/img/garage-trail-training.jpg',
    alt: 'Two riders in motocross gear starting an off-road bike on a grass trail',
  },
  fleet: {
    url: '/assets/img/garage-off-road-fleet.jpg',
    alt: 'A line of vehicles parked along the edge of a forest track',
  },
  bench: {
    url: '/assets/img/garage-repair-bench.jpg',
    alt: 'A mechanic bent over a stripped motorcycle, working on it by hand',
  },
  camp: {
    url: '/assets/img/garage-riders-camp.jpg',
    alt: 'Riders and their bikes at a camp beside a wooded track',
  },
} satisfies Record<string, SeoImage>

export const SITE: SeoSite = {
  origin: 'https://garage.kkfmongolia.com',
  name: 'King of Kings Garage',
  locale: 'en_US',
  defaultImage: IMAGES.trail,
  schema: {
    organization: {
      name: 'King of Kings Garage',
      description:
        'A working off-road workshop in Mongolia and a training floor, where young people learn a trade serving real customers.',
      logo: '/assets/img/logo.png',
      sameAs: ['https://www.facebook.com/profile.php?id=61592414898013'],
      email: 'garage@kkfmongolia.com',
      areaServed: 'Mongolia',
      parentOrganization: { name: 'King of Kings Foundation', url: 'https://kkfmongolia.com' },
    },
    website: {
      description:
        'The off-road workshop, the youth training floor and the tours.',
    },
    // `AutoRepair`, not the generic `LocalBusiness`: the narrower type is what puts a
    // result in the right kind of panel for somebody searching for a workshop.
    //
    // Locality, country and the workshop's own inbox. The street address,
    // telephone and coordinates are all still "to be confirmed" on the contact page — an
    // open blocker on `garage-app-prd.md` — and a plausible-looking address for a recovery
    // service is worse than none, because somebody drives to it.
    business: {
      type: 'AutoRepair',
      name: 'King of Kings Garage',
      description:
        'Off-road recovery, servicing, repair and trail support on one of Mongolia’s great off-road routes — and a training floor for young people.',
      image: IMAGES.bench,
      addressLocality: 'Ulaanbaatar',
      addressCountry: 'MN',
      sameAs: ['https://www.facebook.com/profile.php?id=61592414898013'],
      email: 'garage@kkfmongolia.com',
      parentOrganization: { name: 'King of Kings Foundation', url: 'https://kkfmongolia.com' },
      makesOffer: [
        'Off-road recovery',
        'Overnight and quick support',
        'Servicing and maintenance',
        'Off-road tour recovery',
        'Parts and fitting',
        'Off-road tours and ADV training',
      ],
    },
  },
}

/** Looks a tour up, failing the build rather than describing a page that does not exist. */
const tour = (slug: string) => {
  const found = TOURS.find((entry) => entry.slug === slug)
  if (!found) throw new Error(`[seo] no tour "${slug}" — data/tours.ts and data/seo.ts disagree`)
  return found
}

const STATIC_ROUTES: RouteSeo[] = [
  {
    path: '/',
    name: 'Home',
    title: 'King of Kings Garage — off-road workshop, Mongolia',
    description:
      'Off-road recovery, servicing and trail support on one of Mongolia’s great routes — and a training floor where young people learn the trade doing the work.',
    image: IMAGES.trail,
    changefreq: 'monthly',
    priority: 1,
  },
  {
    path: '/services',
    name: 'Workshop services',
    title: 'Recovery, repair and trail support — Mongolia',
    description:
      'Off-road recovery, overnight and quick support, servicing and maintenance, tour recovery, and parts and fitting — from a workshop that knows the route.',
    image: IMAGES.bench,
    changefreq: 'monthly',
    priority: 0.9,
  },
  {
    path: '/tours',
    name: 'Tours',
    title: 'Off-road tours, ADV training and rentals in Mongolia',
    description:
      'Six ways out: day tours on Jeep roads, hardcore 4WD trails, multi-day motorcycle tours, routes built to your destination, ADV training and bike rentals.',
    image: IMAGES.fleet,
    changefreq: 'monthly',
    priority: 0.9,
    itemList: {
      name: 'Tours, training and rentals',
      items: TOURS.map((entry) => ({ name: entry.name.en, path: `/tours/${entry.slug}` })),
    },
  },
  {
    path: '/programmes',
    name: 'Programmes',
    title: 'Free training for young people — King of Kings Garage',
    description:
      'What a young person can join: an apprentice mechanic track, robotics, creative arts and fabrication, life skills, riding days, and pathways into work.',
    image: IMAGES.camp,
    changefreq: 'monthly',
    priority: 0.9,
  },
  {
    path: '/contact',
    name: 'Contact',
    title: 'Contact King of Kings Garage — recovery or a placement',
    description:
      'A recovery, a placement for a young person, or a look around the workshop — three conversations and one form, answered by someone who can answer it.',
    changefreq: 'yearly',
    priority: 0.6,
  },
  /*
   * The blog index.
   *
   * **Only the index is described here.** Each post is described by `postSeo`, from the
   * markdown itself, and appended in two places — `App.tsx` for the runtime and
   * `vite.config.ts` for the build — because a post comes from `virtual:blog-posts`, which
   * is a bundler concept the Node process that loads this file cannot resolve.
   *
   * `priority` is below the pages somebody came here to do something on: the blog is worth
   * finding and is not what this site is for.
   */
  {
    path: '/blog',
    name: 'Blog',
    title: 'Blog — King of Kings Garage',
    description:
      'Stories from the workshop floor: the apprentices, the trades they are learning, and the men who teach them.',
    changefreq: 'weekly',
    priority: 0.6,
  },
  /*
   * The two shared legal pages (2026-09-19).
   *
   * The words are `@kkf/design`'s — one privacy policy and one set of terms for all five
   * apps — but the site tree entry is each app's own, because the title, the description
   * and the canonical are about THIS host. `audit:seo` checks titles and descriptions for
   * uniqueness within an app, not across the platform, so four apps each carrying a
   * `/privacy` is correct; four identical descriptions would be four pages competing for
   * the same result, which is why each names the thing that site actually asks for.
   *
   * `priority: 0.3` and `changefreq: 'yearly'`: indexed, findable, and not competing with
   * the pages somebody came for.
   */
  {
    path: '/privacy',
    name: 'Privacy Policy',
    title: 'Privacy — King of Kings Garage',
    description:
      'What this site collects when you ask about a tour, a recovery or a placement, and who else handles it. No analytics, no advertising, no tracking.',
    changefreq: 'yearly',
    priority: 0.3,
  },
  {
    path: '/terms',
    name: 'Terms and Conditions',
    title: 'Terms of use — King of Kings Garage',
    description:
      'The terms covering the tours, the training and the youth programmes here, and what King of Kings Foundation can and cannot promise you.',
    changefreq: 'yearly',
    priority: 0.3,
  },
]

/**
 * The six bookable pages.
 *
 * Each carries a `TouristTrip` node built from the page's own facts — the itinerary
 * stages, and the duration exactly as the page words it ("One to three days"), never an
 * ISO duration nobody wrote down. **No price**, on the page or in the structured data:
 * every one of them says "price to be confirmed", and a made-up figure on a booking page
 * is not a placeholder, it is a quotation.
 */
const TOUR_ROUTES: RouteSeo[] = (
  [
    {
      slug: 'off-road-day-tour',
      title: 'Off-road day tour, Mongolia — King of Kings Garage',
      description:
        'A guided day on graded Jeep roads and open steppe, with a guide who has driven the route in every season and a recovery vehicle a radio call away.',
    },
    {
      slug: 'hardcore-4wd-trail',
      title: 'Hardcore 4WD trail, Mongolia — one to three days',
      description:
        'River crossings, loose ascents and technical low-range work, with a lead guide, a recovery vehicle, and a workshop inspection before you leave.',
    },
    {
      slug: 'motorcycle-tour',
      title: 'Motorcycle tours in Mongolia — two to seven days',
      description:
        'Adventure bikes across big country with a support truck behind you, for riders with off-road experience. Two to seven days, June to September.',
    },
    {
      slug: 'tours-by-location',
      title: 'Mongolia tours built around your destination',
      description:
        'Tell us where you want to be — a lake, a dune field, a monastery, a family — and the route, the vehicles and the nights out are built around it.',
    },
    {
      slug: 'adv-training',
      title: 'ADV riding training in Mongolia — one or two days',
      description:
        'Learn to ride the hard stuff before it finds you: technique, recovery and confidence on an adventure bike, taught where you will actually use it.',
    },
    {
      slug: 'motorcycle-rentals',
      title: 'Adventure motorcycle rental, Mongolia — by day or week',
      description:
        'A prepared adventure bike from a workshop that maintains it, hired by the day or the week, and somebody to call if the road goes wrong.',
    },
  ] as const
).map(({ slug, title, description }): RouteSeo => {
  const entry = tour(slug)
  return {
    path: `/tours/${slug}`,
    name: entry.name.en,
    title,
    description,
    image: { url: entry.image, alt: entry.imageAlt.en },
    changefreq: 'monthly',
    priority: 0.7,
    trip: {
      name: entry.name.en,
      description,
      image: { url: entry.image, alt: entry.imageAlt.en },
      // The spec is matched on its ENGLISH term, because that is the one this file can
      // see and the one the data is keyed on.
      duration: (() => {
        const spec = entry.specs.find((s) => s.term.en === 'Duration')?.detail
        return typeof spec === 'string' ? spec : spec?.en
      })(),
      itinerary: entry.itinerary.map((stage) => stage.stage.en),
    },
  }
})

/** Every route this app has. */
export const ROUTES: RouteSeo[] = [...STATIC_ROUTES, ...TOUR_ROUTES]
